import React, { useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import posService from "../../services/pos/pos.service";
import { InputTextarea } from "primereact/inputtextarea";

interface Batch {
  outwardId: number;
  batchId: number;
  batchCode: string;
  quantity: number;
  createdDate: string;
}

interface InwardRequest {
  requestId: number;
  productId: number;
  productName: string;
  requestFromPOS: number;
  requestFromPosCode: string;
  requestToPOS: number;
  requestToPosCode: string;
  totalQuantity: number;
  statusId: number;
  status: string;
  respondedBy: number | null;
  respondedDate: string;
  note: string;
  batches: Batch[];
}

interface InwardRequestsTabProps {
  data: InwardRequest[];
  loading: boolean;
  onRefresh: () => void;
  showMessage: (summary: string, detail: string, severity: any) => void;
  selectedPosIds?: number[];
}

const InwardRequestsTab: React.FC<InwardRequestsTabProps> = ({
  data,
  loading,
  onRefresh,
  showMessage,
  selectedPosIds = [],
}) => {
  const [keywordSearchInward, setKeywordSearchInward] = useState<string>("");

const [acceptDialogVisible, setAcceptDialogVisible] = useState<boolean>(false);
const [selectedRequest, setSelectedRequest] = useState<InwardRequest | null>(null);
const [acceptNote, setAcceptNote] = useState<string>("");
const [submitting, setSubmitting] = useState<boolean>(false);

  const handleInwardSearch = (value: string) => {
    setKeywordSearchInward(value);
  };

  const getSentQuantity = (request: InwardRequest) => {
    if (request.batches && request.batches.length > 0) {
      return request.batches.reduce((sum, b) => sum + (Number(b.quantity) || 0), 0);
    }
    return request.totalQuantity ?? 0;
  };

  const filteredInwardRequests = useMemo(() => {
    if (!keywordSearchInward) return data;

    const searchLower = keywordSearchInward.toLowerCase();
    return data.filter(
      (request) =>
        request.productName.toLowerCase().includes(searchLower) ||
        request.requestFromPosCode.toLowerCase().includes(searchLower) ||
        request.requestToPosCode.toLowerCase().includes(searchLower) ||
        request.status.toLowerCase().includes(searchLower) ||
        getSentQuantity(request).toString().includes(searchLower)
    );
  }, [data, keywordSearchInward]);

  const handleAccept = (request: InwardRequest) => {
    setSelectedRequest(request);
    setAcceptNote("");
    setAcceptDialogVisible(true);
  };


  const handleAcceptConfirm = async () => {
  if (!selectedRequest) return;

  try {
    setSubmitting(true);
    
    const payload = {
      requestId: selectedRequest.requestId,
      note: acceptNote.trim()
    };

    const response = await posService.updateinwardrequestAccept(payload);
    
    if (response) {
      showMessage("Success", "Inward request accepted successfully", "success");
      setAcceptDialogVisible(false);
      setAcceptNote("");
      setSelectedRequest(null);
      onRefresh();
    } else {
      showMessage("Error", "Failed to accept inward request", "error");
    }
  } catch (error) {
    console.error("Error accepting inward request:", error);
    showMessage("Error", "Failed to accept inward request", "error");
  } finally {
    setSubmitting(false);
  }
};

  const handleReject = (request: InwardRequest) => {
    console.log("Reject request:", request);
    // TODO: Implement reject functionality
  };

  const dateBodyTemplate = (rowData: InwardRequest) => {
    if (!rowData.respondedDate) return "-";

    const date = new Date(rowData.respondedDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };
 
  const actionBodyTemplate = (rowData: InwardRequest) => {
    return (
      <div className="flex gap-2">
        <Button
          label="Accept"
          icon="pi pi-check"
          className="px-1 py-2 text-sm"
          onClick={() => handleAccept(rowData)}
        />
        <Button
          label="Reject"
          icon="pi pi-times"
          severity="danger"
          className="px-1 py-2 text-sm"
          onClick={() => handleReject(rowData)}
          disabled
        />
      </div>
    );
  };

  return (
    <div className="col-12">
      <div className="flex justify-content-between align-items-center mb-3">
        <div className="p-input-icon-left w-full md:w-4">
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={keywordSearchInward}
            onChange={(e) => handleInwardSearch(e.target.value)}
            placeholder="Search Inward Requests"
            className="w-full"
            style={{
              border: "1px solid #75bb8aff",
              boxShadow: "0 0 0 1px #75bb8aff",
            }}
          />
        </div>
      </div>

      {filteredInwardRequests && filteredInwardRequests.length > 0 ? (
        <DataTable
          value={filteredInwardRequests}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column
            field="respondedDate"
            header="Sent Date"
            body={dateBodyTemplate}
            sortable
            style={{ width: "12%" }}
          />
          <Column
            field="productName"
            header="Product Name"
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="totalQuantity"
            header="Quantity"
            body={(rowData: InwardRequest) => getSentQuantity(rowData)}
            sortable
            style={{ width: "8%" }}
          />
          <Column
            field="requestToPosCode"
            header="Sent By"
            sortable
            style={{ width: "10%" }}
          />
          <Column
            field="requestFromPosCode"
            header="Sent To"
            sortable
            style={{ width: "10%" }}
          />
          <Column
            header="Actions"
            body={actionBodyTemplate}
            style={{ width: "16%" }}
          />
        </DataTable>
      ) : (
        <div className="text-center p-4">
          <p className="text-gray-500">
            {keywordSearchInward
              ? "No inward requests found matching your search."
              : "No inward requests found."}
          </p>
        </div>
      )}

      {/* NEW: Accept Dialog */}
    {/* NEW: Accept Dialog */}
<Dialog
  header="Accept Inward Request"
  visible={acceptDialogVisible}
  style={{ width: "600px" }}
  onHide={() => {
    if (!submitting) {
      setAcceptDialogVisible(false);
      setAcceptNote("");
      setSelectedRequest(null);
    }
  }}
  breakpoints={{ "960px": "75vw", "641px": "90vw" }}
  footer={
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={() => {
          setAcceptDialogVisible(false);
          setAcceptNote("");
          setSelectedRequest(null);
        }}
        className="p-button-text"
        disabled={submitting}
      />
      <Button
        label="Accept"
        icon="pi pi-check"
        onClick={handleAcceptConfirm}
        loading={submitting}
        autoFocus
      />
    </div>
  }
>
  <div className="flex flex-column gap-3">
    {/* Product Info Section */}
    <div className="p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
      <p className="mb-2">
        <strong>Product:</strong> {selectedRequest?.productName}
      </p>
      <p className="mb-2">
        <strong>Total Quantity:</strong> {selectedRequest ? getSentQuantity(selectedRequest) : 0}
      </p>
      <p className="mb-0">
        <strong>From:</strong> {selectedRequest?.requestToPosCode}
      </p>
    </div>

    {/* Batch Details Section */}
    {selectedRequest?.batches && selectedRequest.batches.length > 0 && (
      <div>
        <h4 className="mb-2" style={{ fontSize: "1rem", fontWeight: "600" }}>
          Batch Details
        </h4>
        <div className="p-3" style={{ 
          backgroundColor: "#ffffff", 
          border: "1px solid #dee2e6", 
          borderRadius: "6px" 
        }}>
          <DataTable 
            value={selectedRequest.batches} 
            size="small"
            showGridlines
          >
            <Column 
              field="batchCode" 
              header="Batch Code" 
              style={{ width: "50%" }}
            />
            <Column 
              field="quantity" 
              header="Quantity" 
              style={{ width: "50%" }}
              body={(rowData: Batch) => (
                <span style={{ fontWeight: "500" }}>
                  {rowData.quantity}
                </span>
              )}
            />
          </DataTable>
        </div>
      </div>
    )}
    
    {/* Note Input Section */}
    <div className="flex flex-column gap-2">
      <label htmlFor="accept-note">
        Note (Optional)
      </label>
      <InputTextarea
        id="accept-note"
        value={acceptNote}
        onChange={(e) => setAcceptNote(e.target.value)}
        rows={4}
        placeholder="Enter acceptance note (optional)"
        className="w-full"
        autoResize
        disabled={submitting}
        style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
      />
    </div>
  </div>
</Dialog>

    </div>
  );
};

export default InwardRequestsTab;
